import { Alert } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import LocationHistory from '@/insights/assets/locationHistory/LocationHistory'
import { buildQuery } from '@/insights/common/queryUtils'
import {
  ApiService,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

import { LiveRouteMap, MapSkeleton, RouteSummary } from './LiveRouteMap'
import { useLiveRoute } from './liveRouteStore'
import type { RouteHistory, RouteHistoryResponse } from './types'

export interface RouteHistoryMapParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

/**
 * Where an asset went over a date range, on a map — the asset counterpart to the tracker
 * map's history trail. Takes the same parameters as Location History, so it shares that
 * insight's controls, validation and caption.
 */
const RouteHistoryMap: InsightDefinition<RouteHistoryMapParams, RouteHistory> = {
  active: true,
  slug: 'asset/route-history',
  category: 'asset',
  name: 'Route History - Map',
  description: 'Map where an asset moved over a date range, with door openings and alerts',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: LocationHistory.configuration,

  load: async (api: ApiService, parameters: RouteHistoryMapParams) => {
    // simpleGet, not get: get spreads the body into its result and expects the rows under a
    // `data` key, but this endpoint returns one object ({ from, to, points, ... }), so
    // response.data would always be undefined.
    let route: RouteHistoryResponse
    try {
      route = await api.simpleGet<RouteHistoryResponse>({
        path: `/insights/asset/route-history?${buildQuery(parameters)}`,
        origin: 'RouteHistoryMap',
      })
    } catch (error) {
      return { code: InsightStatusCode.ERROR, message: error instanceof Error ? error.message : String(error) }
    }
    if (!route.points.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    // Absent from an API that predates them.
    return { ...route, movements: route.movements ?? [], doors: route.doors ?? [], power: route.power ?? [] }
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <MapSkeleton />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (!data || isInsightStatus(data) || !data.points.length) {
      return null
    }
    return (
      <LiveRouteMap
        id={configuration.id}
        data={data}
        query={buildQuery(configuration.parameters as RouteHistoryMapParams)}
        companyId={(configuration.parameters as RouteHistoryMapParams).companyId}
      />
    )
  },

  // Location History's caption reads only the configuration (company, asset, dates).
  Caption: ({ configuration, status }) => {
    const LocationCaption = LocationHistory.Caption
    return LocationCaption ? <LocationCaption configuration={configuration} status={status} /> : ''
  },

  Footer: ({ data: loaded, status, configuration }) => {
    // Counts follow the map while it follows the asset live.
    const data = useLiveRoute(configuration.id, loaded)
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No location data for this period</div>
    }
    if (!data || isInsightStatus(data) || status.code !== InsightStatusCode.DISPLAYING) {
      return ''
    }
    return <RouteSummary route={data} />
  },
}

export default RouteHistoryMap
